import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import rackTemplateServices from "app/services/rack-template-services";

const KINDS = [
    { value: "picking", label: "Picking" },
    { value: "reserva", label: "Reserva" },
    { value: "recepcion", label: "Recepción" },
    { value: "despacho", label: "Despacho" },
    { value: "staging", label: "Staging" },
    { value: "devoluciones", label: "Devoluciones" },
];

const EMPTY = {
    name: "",
    levels: 4,
    positions: 3,
    footprint_w: 1,
    footprint_h: 1,
    kind: "picking",
    color: "#7352C7",
};

// Los tipos de rack de la empresa: el molde que después se estampa en el plano.
const RackTypesModal = ({ open, onClose, onChanged }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        rackTemplateServices
            .getTemplates("?include_inactive=true")
            .then((data) => setTemplates(data || []))
            .catch(() => setTemplates([]));
    };

    useEffect(() => {
        if (!open) return;
        load();
        setForm(EMPTY);
        setEditingId(null);
    }, [open]);

    const handleChange = (field) => (event) => setForm({ ...form, [field]: event.target.value });

    const total = (Number(form.levels) || 0) * (Number(form.positions) || 0);

    const submit = async () => {
        try {
            setSaving(true);
            if (editingId) {
                await rackTemplateServices.updateTemplate(editingId, form);
                enqueueSnackbar("Tipo actualizado", { variant: "success" });
            } else {
                await rackTemplateServices.createTemplate(form);
                enqueueSnackbar("Tipo creado", { variant: "success" });
            }
            setForm(EMPTY);
            setEditingId(null);
            load();
            onChanged?.();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo guardar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const edit = (template) => {
        setEditingId(template.id);
        setForm({
            name: template.name,
            levels: template.levels,
            positions: template.positions,
            footprint_w: template.footprint_w,
            footprint_h: template.footprint_h,
            kind: template.kind,
            color: template.color || "#7352C7",
        });
    };

    const remove = async (template) => {
        try {
            await rackTemplateServices.deleteTemplate(template.id);
            enqueueSnackbar("Tipo eliminado", { variant: "success" });
            load();
            onChanged?.();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo eliminar", { variant: "error" });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Tipos de rack
                <Typography variant="body2" color="text.secondary">
                    El molde de cada mueble: cuántos niveles tiene, cuántos huecos por nivel y cuántas celdas del plano
                    ocupa
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nombre"
                            value={form.name}
                            onChange={handleChange("name")}
                            placeholder="Selectivo 4 niveles"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Niveles"
                            value={form.levels}
                            onChange={handleChange("levels")}
                            helperText="A = piso"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Huecos por nivel"
                            value={form.positions}
                            onChange={handleChange("positions")}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Ancho"
                            value={form.footprint_w}
                            onChange={handleChange("footprint_w")}
                            helperText="celdas"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Fondo"
                            value={form.footprint_h}
                            onChange={handleChange("footprint_h")}
                            helperText="celdas"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Tipo"
                            value={form.kind}
                            onChange={handleChange("kind")}
                        >
                            {KINDS.map((kind) => (
                                <MenuItem key={kind.value} value={kind.value}>
                                    {kind.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="color"
                            label="Color"
                            value={form.color}
                            onChange={handleChange("color")}
                        />
                    </Grid>
                    <Grid item xs={6} sm={5}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                            <LoadingButton
                                variant="contained"
                                loading={saving}
                                onClick={submit}
                                disabled={!form.name || total < 1}
                            >
                                {editingId ? "Guardar" : "Agregar"}
                            </LoadingButton>
                            {editingId && (
                                <Button
                                    onClick={() => {
                                        setEditingId(null);
                                        setForm(EMPTY);
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                            <Chip size="small" label={`${total} posiciones`} />
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {templates.length === 0 ? (
                    <Alert severity="info">Todavía no hay tipos de rack.</Alert>
                ) : (
                    <Stack spacing={1}>
                        {templates.map((template) => (
                            <Paper key={template.id} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "3px",
                                            bgcolor: template.color || "grey.400",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {template.name}
                                            {!template.active && (
                                                <Chip size="small" label="inactivo" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {template.levels} niveles × {template.positions} huecos ={" "}
                                            {template.positions_count} posiciones · huella {template.footprint_w}×
                                            {template.footprint_h} · {template.kind}
                                            {template.racks_count > 0 && ` · ${template.racks_count} puestos`}
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => edit(template)}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            template.racks_count > 0
                                                ? "Hay racks puestos con este tipo: desactivalo en vez de borrarlo"
                                                : "Eliminar"
                                        }
                                    >
                                        <IconButton size="small" onClick={() => remove(template)}>
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RackTypesModal;
